import { Container, Sprite, Text } from 'pixi.js';
import type { LayoutProfile, ResolvedTheme } from '../types';
import type { TextureStore } from './textures';

/**
 * Game identity in the scene header (ClefDev logo handoff v1):
 *  - base title → the composite PNG (dimensional game lettering + themed CLEFDEV.COM signature, 8:3);
 *  - operator-renamed game (`theme.customTitle`) → the title as live text over the separate signature SVG;
 *  - missing PNG → the same live-text fallback; missing signature → plain "CLEFDEV.COM" text.
 * Static: no spin/win reactions, no interaction. Box comes from `layout.<profile>.logo` (x, y, width).
 */
export class LogoLayer extends Container {
  private theme: ResolvedTheme;
  private readonly textures: TextureStore;
  private composite: Sprite | null = null;
  private signature: Sprite | null = null;
  private readonly title: Text;
  private readonly signatureText: Text;
  private box: LayoutProfile['logo'] | null = null;
  private portrait = false;

  constructor(o: { theme: ResolvedTheme; textures: TextureStore }) {
    super();
    this.theme = o.theme;
    this.textures = o.textures;
    this.eventMode = 'none';
    this.title = new Text({
      text: '',
      style: { fontFamily: o.theme.fonts.display.family, fontSize: 32, fontWeight: '800', fill: 0xffffff, align: 'center', wordWrap: true, wordWrapWidth: 320, breakWords: true, lineHeight: 36, dropShadow: { alpha: 0.6, blur: 4, distance: 2, color: 0x000000 } },
    });
    this.title.anchor.set(0.5, 0);
    this.title.resolution = 2;
    this.signatureText = new Text({ text: 'CLEFDEV.COM', style: { fontFamily: o.theme.fonts.display.family, fontSize: 14, fontWeight: '800', fill: 0xffffff, letterSpacing: 2, align: 'center' } });
    this.signatureText.anchor.set(0.5, 0);
    this.signatureText.resolution = 2;
    this.build();
  }

  private build(): void {
    this.removeChildren();
    this.composite?.destroy();
    this.signature?.destroy();
    this.composite = null;
    this.signature = null;
    const logoTex = this.theme.assets.logo ? this.textures.image(this.theme.assets.logo) : null;
    const sigTex = this.theme.assets.brandSignature ? this.textures.image(this.theme.assets.brandSignature) : null;
    const custom = this.theme.customTitle;
    if (logoTex && !custom) {
      this.composite = new Sprite(logoTex);
      this.addChild(this.composite);
      return;
    }
    // Live text title (operator rename or PNG unavailable) + separate themed signature.
    this.title.text = custom ?? this.theme.name;
    this.title.style.fontFamily = this.theme.fonts.display.family;
    this.addChild(this.title);
    if (sigTex) {
      this.signature = new Sprite(sigTex);
      this.addChild(this.signature);
    } else {
      this.signatureText.style.fontFamily = this.theme.fonts.display.family;
      this.addChild(this.signatureText);
    }
  }

  setTheme(theme: ResolvedTheme): void {
    this.theme = theme;
    this.build();
    if (this.box) this.layoutBox(this.box, this.portrait);
  }

  /** Place the identity into the profile's logo box; hidden when the profile has none. */
  layout(profile: LayoutProfile): void {
    this.portrait = profile.designWidth < profile.designHeight;
    this.box = profile.logo ?? null;
    this.visible = !!this.box;
    if (this.box) this.layoutBox(this.box, this.portrait);
  }

  private layoutBox(L: NonNullable<LayoutProfile['logo']>, portrait: boolean): void {
    const w = L.width;
    if (this.composite) {
      // Composite PNG is 8:3; never stretched, never cropped.
      const ratio = this.composite.texture.height / Math.max(1, this.composite.texture.width);
      this.composite.width = w;
      this.composite.height = w * ratio;
      this.composite.position.set(L.x, L.y);
      return;
    }
    // Custom / fallback lockup: wrapped title on top, signature (≈175 px wide, ≥ 55% of the box) underneath.
    const size = Math.max(18, Math.round(w * (portrait ? 0.1 : 0.105)));
    this.title.style.fontSize = size;
    this.title.style.lineHeight = Math.round(size * 1.1);
    this.title.style.wordWrapWidth = w;
    this.title.position.set(L.x + w / 2, L.y);
    const sigW = Math.min(175, Math.round(w * 0.55));
    const gap = Math.round(size * 0.35);
    const y = L.y + this.title.height + gap;
    if (this.signature) {
      const ratio = this.signature.texture.height / Math.max(1, this.signature.texture.width);
      this.signature.width = sigW;
      this.signature.height = sigW * ratio;
      this.signature.position.set(L.x + (w - sigW) / 2, y);
    } else {
      this.signatureText.style.fontSize = Math.max(11, Math.round(sigW * 0.09));
      this.signatureText.position.set(L.x + w / 2, y);
    }
  }
}
